import { ref, computed, watch, type Ref } from "vue";
import { api } from "@app/data/index";
import { type FloorPlan } from "@lemmonhq/types";

export function useFloorPlanEditor(floorPlans: Ref<FloorPlan[]>) {
  const activeFloorPlan = ref<string>();
  const originalFloorPlan = ref<FloorPlan | null>(null);
  const currentFloorPlan = ref<FloorPlan | null>(null);

  const isDirty = computed(() => {
    if (!originalFloorPlan.value || !currentFloorPlan.value) return false;
    return (
      JSON.stringify(originalFloorPlan.value) !==
      JSON.stringify(currentFloorPlan.value)
    );
  });

  const initializeFloorPlan = () => {
    if (!activeFloorPlan.value && floorPlans.value.length > 0) {
      activeFloorPlan.value = floorPlans.value[0]?.id;
    }
    if (floorPlans.value.length > 0) {
      const initial = floorPlans.value.find(
        (el) => el.id === activeFloorPlan.value,
      );
      if (initial) {
        originalFloorPlan.value = JSON.parse(JSON.stringify(initial));
        currentFloorPlan.value = JSON.parse(JSON.stringify(initial));
      }
    }
  };

  watch(floorPlans, initializeFloorPlan, { immediate: true });

  const handleFloorPlanChange = (updatedFloorPlan: FloorPlan) => {
    currentFloorPlan.value = updatedFloorPlan;
  };

  const handleSaveFloorPlan = async () => {
    if (!currentFloorPlan.value) return;
    await api.updateFloorPlan(
      currentFloorPlan.value.id,
      currentFloorPlan.value,
    );
    originalFloorPlan.value = JSON.parse(
      JSON.stringify(currentFloorPlan.value),
    );
    const index = floorPlans.value.findIndex(
      (fp) => fp.id === currentFloorPlan.value!.id,
    );
    if (index !== -1) {
      floorPlans.value[index] = JSON.parse(
        JSON.stringify(currentFloorPlan.value),
      );
    }
  };

  const handleResetFloorPlan = () => {
    if (originalFloorPlan.value) {
      currentFloorPlan.value = JSON.parse(
        JSON.stringify(originalFloorPlan.value),
      );
    }
  };

  const handleFloorPlanSelect = (floorPlanId: string) => {
    const selected = floorPlans.value.find((fp) => fp.id === floorPlanId);
    if (selected) {
      activeFloorPlan.value = floorPlanId;
      originalFloorPlan.value = JSON.parse(JSON.stringify(selected));
      currentFloorPlan.value = JSON.parse(JSON.stringify(selected));
    }
  };

  return {
    activeFloorPlan,
    currentFloorPlan,
    isDirty,
    handleFloorPlanChange,
    handleSaveFloorPlan,
    handleResetFloorPlan,
    handleFloorPlanSelect,
    initializeFloorPlan,
  };
}
